import { useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { Button, Card, Icon, Pill, Stack, Text } from '@/design-system'
import { PageHeader } from '@/app/layouts/PageHeader'
import { NotFoundScreen } from '@/features/system/NotFoundScreen'
import { formatMoney, fromMajor, money } from '@/lib/money'
import { cn } from '@/lib/cn'
import { FLIGHTS, FLIGHT_CASHBACK, findFlight } from './travel-data'
import checkout from '@/app/layouts/CheckoutLayout.module.css'
import select from '@/features/onboarding/SelectCard.module.css'
import { BRAND } from '@/brand/brand'

interface Extra {
  readonly id: string
  readonly title: string
  readonly body: string
  readonly price: number
  readonly locked?: boolean
}

const EXTRAS: readonly Extra[] = [
  { id: 'seat', title: 'Seat 14A', body: 'Window · extra legroom', price: 42 },
  { id: 'bag', title: 'Checked bag', body: '23 kg · included in your fare', price: 0, locked: true },
  { id: 'extra', title: 'Extra bag', body: '23 kg', price: 65 },
  { id: 'insurance', title: 'Travel insurance', body: 'Medical and cancellation', price: 28 },
  { id: 'esim', title: 'Travel eSIM', body: '5 GB · 15 days', price: 14 },
]

export function FlightCheckoutScreen() {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const flight = findFlight(params.get('flight') ?? '') ?? FLIGHTS[0]
  const [chosen, setChosen] = useState<readonly string[]>(['seat', 'bag'])
  const [payInFour, setPayInFour] = useState(false)

  const model = useMemo(() => {
    if (!flight) return null
    const fare = fromMajor(flight.fare, 'USD')
    const taxes = fromMajor(flight.taxes, 'USD')
    const extras = EXTRAS.filter((extra) => chosen.includes(extra.id)).reduce(
      (sum, extra) => sum + extra.price * 100,
      0,
    )
    const total = money(fare.minorUnits + taxes.minorUnits + extras, 'USD')
    return {
      fare,
      taxes,
      extras: money(extras, 'USD'),
      total,
      cashback: money(Math.round(total.minorUnits * FLIGHT_CASHBACK), 'USD'),
      instalment: money(Math.round(total.minorUnits / 4), 'USD'),
    }
  }, [flight, chosen])

  if (!flight || !model) return <NotFoundScreen />

  const toggle = (id: string) =>
    setChosen((current) =>
      current.includes(id) ? current.filter((item) => item !== id) : [...current, id],
    )

  return (
    <>
      <PageHeader
        title="Review and pay"
        subtitle={`${flight.airline} · 12 Oct · 1 passenger · ${flight.route}`}
        actions={
          <>
            <Button variant="ghost" size="md" iconStart="arrow_back" to={`/travel/flights/${flight.slug}`}>
              Back to the flight
            </Button>
            <Pill tone="warning">Price held for 09:42</Pill>
          </>
        }
      />

      <div className={checkout.grid}>
        <Card tone="light">
          <Text variant="caption" weight="semibold">
            Seat, bags and extras
          </Text>
          <Stack gap={2} className="pb-mt-4">
            {EXTRAS.map((extra) => {
              const isOn = chosen.includes(extra.id)
              return (
                <button
                  key={extra.id}
                  type="button"
                  className={cn(select.option, isOn && select.selected)}
                  onClick={() => !extra.locked && toggle(extra.id)}
                  aria-pressed={isOn}
                  disabled={extra.locked}
                >
                  <Stack grow gap={1} align="start">
                    <Text variant="caption" weight="semibold">
                      {extra.title}
                    </Text>
                    <Text variant="eyebrow" tone="secondary">
                      {extra.body}
                    </Text>
                  </Stack>
                  <Text variant="caption" weight="semibold" numeric>
                    {extra.price === 0 ? 'Included' : `+${formatMoney(fromMajor(extra.price, 'USD'))}`}
                  </Text>
                  <Icon
                    name={isOn ? 'check_circle' : 'radio_button_unchecked'}
                    size={20}
                    filled={isOn}
                    className={cn(select.check, isOn && select.checked)}
                  />
                </button>
              )
            })}
          </Stack>
        </Card>

        <Stack gap={3} className={checkout.summary}>
          <Card tone="light">
            <Text variant="caption" weight="semibold">
              Fare summary
            </Text>
            <Stack gap={0} className="pb-mt-3">
              {[
                ['Base fare', formatMoney(model.fare)],
                ['Taxes and charges', formatMoney(model.taxes)],
                ['Seat, bags and extras', formatMoney(model.extras)],
                [`${BRAND.name} cashback · 1%`, `+${formatMoney(model.cashback)}`],
              ].map(([label, value], index) => (
                <div key={label} className={checkout.line}>
                  <Text variant="caption" tone="secondary">
                    {label}
                  </Text>
                  <Text
                    variant="caption"
                    weight="semibold"
                    tone={index === 3 ? 'positive' : 'primary'}
                    numeric
                  >
                    {value}
                  </Text>
                </div>
              ))}
              <div className={checkout.ruleLight} />
              <div className={checkout.line}>
                <Text variant="caption" weight="semibold">
                  {payInFour ? 'Due today' : 'Total'}
                </Text>
                <Text variant="title" weight="bold" numeric>
                  {formatMoney(payInFour ? model.instalment : model.total)}
                </Text>
              </div>
            </Stack>

            <Stack gap={2} className="pb-mt-4">
              {[
                {
                  id: 'card',
                  icon: 'credit_card',
                  title: `${BRAND.name} ••4568`,
                  body: '1% back · no FX fee',
                },
                {
                  id: 'four',
                  icon: 'calendar_month',
                  title: `Pay in 4 × ${formatMoney(model.instalment)}`,
                  body: 'No interest · first payment today',
                },
              ].map((option) => {
                const isOn = (option.id === 'four') === payInFour
                return (
                  <Card key={option.id} tone={isOn ? 'dark' : 'plain'} padded={false}>
                    <Stack
                      direction="row"
                      align="center"
                      gap={3}
                      style={{ padding: 'var(--pb-space-4)' }}
                    >
                      <Icon name={option.icon} size={20} />
                      <Stack grow gap={0}>
                        <Text variant="caption" weight="semibold" tone={isOn ? 'onDark' : 'primary'}>
                          {option.title}
                        </Text>
                        <Text variant="eyebrow" tone={isOn ? 'onDarkMuted' : 'secondary'}>
                          {option.body}
                        </Text>
                      </Stack>
                      <Button
                        variant={isOn ? 'primary' : 'outline'}
                        size="sm"
                        onClick={() => setPayInFour(option.id === 'four')}
                      >
                        {isOn ? 'Selected' : 'Select'}
                      </Button>
                    </Stack>
                  </Card>
                )
              })}
            </Stack>

            <Card.Footer>
              <Button
                size="lg"
                block
                iconStart="lock"
                onClick={() =>
                  navigate(`/travel/flights/done?flight=${flight.slug}&total=${model.total.minorUnits}`, {
                    replace: true,
                  })
                }
              >
                Pay {formatMoney(payInFour ? model.instalment : model.total)}
              </Button>
            </Card.Footer>
          </Card>
        </Stack>
      </div>
    </>
  )
}
